import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {InspectionScheduleDrawer} from 'src/app/inspections/schedules/InspectionScheduleDrawer';
import {useInspectionScheduleColumns} from 'src/app/inspections/schedules/useInspectionScheduleColumns';
import {useInspectionScheduleCrud} from 'src/app/inspections/schedules/useInspectionScheduleCrud';
import {useScheduleBulkActive} from 'src/app/inspections/schedules/useScheduleBulkActive';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetInspectionSchedulesBySiteQuery} from 'src/redux/queries/inspectionSchedule';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export interface InspectionSchedulesSectionProps {
  siteId: number;
  siteTimeZone?: string;
  canManage: boolean;
  canUpdateSite: boolean;
}

export const InspectionSchedulesSection: React.FC<
  InspectionSchedulesSectionProps
> = ({siteId, siteTimeZone, canManage, canUpdateSite}) => {
  const {
    data: schedules = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetInspectionSchedulesBySiteQuery(siteId);

  const crud = useInspectionScheduleCrud({canManage, siteId});
  const [drawerScheduleId, setDrawerScheduleId] = useState<number>();

  const hasTimeZone = !!siteTimeZone;

  const columns = useInspectionScheduleColumns({includeSite: false});

  const tableState = useTableState({
    identifier: `inspection-schedules-for-site-${siteId}`,
    columns,
    defaultDensity: 'xs',
  });

  const bulkActive = useScheduleBulkActive({rows: schedules, canManage});

  const {table} = useTable({
    ...tableState,
    rowSelection: bulkActive.rowSelection,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableGrouping: false,
      enableColumnFilterModes: false,
      enableRowSelection: bulkActive.enableRowSelection,
      columns,
      data: schedules,
      getRowId: row => String(row.id),
      onRowSelectionChange: bulkActive.setRowSelection,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => setDrawerScheduleId(row.original.id)}
          />
          {canManage && (
            <TableRowActions.Edit onClick={() => crud.openEdit(row.original)} />
          )}
          {canManage && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => crud.requestDelete(row.original)}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderEmptyRowsFallback: () => (
        <NoItemsPanel noItemsText="No recurrence schedules for this site." />
      ),
      renderTopToolbarCustomActions: canManage
        ? () => (
            <TableToolbar>
              <TableToolbar.Actions>
                {hasTimeZone && (
                  <TableToolbar.Action
                    variant="create"
                    onClick={crud.openCreate}>
                    Add schedule
                  </TableToolbar.Action>
                )}
                {bulkActive.actions}
              </TableToolbar.Actions>
            </TableToolbar>
          )
        : undefined,
    },
    totalCount: schedules.length,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <SectionCard title="Inspection Schedules">
      {!hasTimeZone && (
        <InfoAlert variant="warning" title="Time zone required" mb="md">
          Recurrence schedules use the site&apos;s local time zone to compute
          due dates. Set a time zone for this site before adding a schedule.
          {canUpdateSite && (
            <NavAnchor
              text="Edit site"
              component={Link}
              to={PathUtility.SitesEdit(siteId)}
              ml="xs"
            />
          )}
        </InfoAlert>
      )}

      {table}

      <InspectionScheduleDrawer
        opened={drawerScheduleId !== undefined}
        onClose={() => setDrawerScheduleId(undefined)}
        scheduleId={drawerScheduleId}
      />

      {bulkActive.confirmDialog}
      {crud.dialogs}
    </SectionCard>
  );
};
